// src/storage/StorageAdapter.js

/**
 * Abstract storage adapter interface for EventIndexer.
 * Implementations must handle all async operations.
 */
class StorageAdapter {
  /**
   * Initialize storage with configuration.
   * @param {Object} config - Storage configuration
   * @param {string} config.dbName - Database name
   * @param {number} config.version - Schema version
   * @param {Object[]} config.eventTypes - Event types from ABI with indexed params
   * @returns {Promise<void>}
   */
  async initialize(config) {
    throw new Error('StorageAdapter.initialize() must be implemented');
  }

  /**
   * Store multiple events atomically.
   * @param {IndexedEvent[]} events - Events to store
   * @returns {Promise<void>}
   */
  async putEvents(events) {
    throw new Error('StorageAdapter.putEvents() must be implemented');
  }

  /**
   * Get single event by type and ID.
   * @param {string} type - Event type name
   * @param {string} id - Event ID (txHash-logIndex)
   * @returns {Promise<IndexedEvent|null>}
   */
  async getEvent(type, id) {
    throw new Error('StorageAdapter.getEvent() must be implemented');
  }

  /**
   * Query events with filters, sorting, pagination.
   * @param {string} type - Event type name
   * @param {QueryOptions} options - Query options
   * @returns {Promise<QueryResult>}
   */
  async queryEvents(type, options) {
    throw new Error('StorageAdapter.queryEvents() must be implemented');
  }

  /**
   * Count events matching filter.
   * @param {string} type - Event type name
   * @param {Object} where - Filter conditions
   * @returns {Promise<number>}
   */
  async countEvents(type, where) {
    throw new Error('StorageAdapter.countEvents() must be implemented');
  }

  /**
   * Delete events by IDs.
   * @param {string} type - Event type name
   * @param {string[]} ids - Event IDs to delete
   * @returns {Promise<void>}
   */
  async deleteEvents(type, ids) {
    throw new Error('StorageAdapter.deleteEvents() must be implemented');
  }

  /**
   * Delete all events after a block number (for reorg recovery).
   * @param {number} blockNumber - Delete events after this block
   * @returns {Promise<void>}
   */
  async deleteEventsAfterBlock(blockNumber) {
    throw new Error('StorageAdapter.deleteEventsAfterBlock() must be implemented');
  }

  /**
   * Get sync state.
   * @returns {Promise<SyncState|null>}
   */
  async getSyncState() {
    throw new Error('StorageAdapter.getSyncState() must be implemented');
  }

  /**
   * Update sync state.
   * @param {SyncState} state - New sync state
   * @returns {Promise<void>}
   */
  async setSyncState(state) {
    throw new Error('StorageAdapter.setSyncState() must be implemented');
  }

  /**
   * Get schema version.
   * @returns {Promise<number>}
   */
  async getVersion() {
    throw new Error('StorageAdapter.getVersion() must be implemented');
  }

  /**
   * Set schema version.
   * @param {number} version - New version
   * @returns {Promise<void>}
   */
  async setVersion(version) {
    throw new Error('StorageAdapter.setVersion() must be implemented');
  }

  /**
   * Clear all data.
   * @returns {Promise<void>}
   */
  async clear() {
    throw new Error('StorageAdapter.clear() must be implemented');
  }

  /**
   * Close connection and cleanup.
   * @returns {Promise<void>}
   */
  async close() {
    throw new Error('StorageAdapter.close() must be implemented');
  }
}

export default StorageAdapter;
